import React, {useEffect, useState, useCallback} from 'react'
import PropTypes from 'prop-types'
import {useLocation} from 'react-router-dom'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {useFetchOneEntry} from 'hec/hooks/useFetchOneEntry'
import {useArticlePagination} from './useArticlePagination'
import builderConfig from 'hec/builder/model-map'
import {generateArticleCard} from './utils'
import {Loader} from '@/components/loader'
import {Blocks} from '@builder.io/sdk-react'
import {PaginatedGridDisplay} from 'hec/components/paginated-grid-display'
import isEqual from 'lodash/isEqual'

export const DynamicGrid = (props) => {
    const {attributes, builderBlock, itemsToShowPerLoad} = props
    const location = useLocation()
    const config = getConfig()
    const articleTopic = new URLSearchParams(location.search).get('topic')

    const [articleCards, setArticleCards] = useState([])

    const {
        data: articleTopicData,
        isLoading: articleTopicDataLoading,
        error: articleTopicDataError
    } = useFetchOneEntry({
        queryKey: ['learning-center-article-grid-topic', articleTopic],
        options: {
            model: builderConfig.learningCenterArticleTopic,
            apiKey: config.app.builder.api,
            fields: 'id',
            query: {
                name: articleTopic
            }
        },

        enabled: !!articleTopic
    })

    const articleTopicId = articleTopicData?.id
    const queryAllArticles = !articleTopic

    const {
        showNextEntries,
        displayedArticles,
        displayedArticlesCount,
        showAll,
        totalItems,
        getAllEntries,
        isLoading: articlesDataLoading,
        error: articlesDataError,
        allEntriesDisplayed
    } = useArticlePagination({
        perPageLimit: itemsToShowPerLoad,
        articleTopicId,
        queryAllArticles,
        filterFunction: (article) => article?.data?.page?.value?.published === 'published'
    })

    const generateCardsFromArticleData = useCallback(() => {
        return displayedArticles.map((article) => generateArticleCard(article))
    }, [displayedArticles])

    useEffect(() => {
        if (displayedArticles && displayedArticles.length > 0) {
            const articleCardBlocks = generateCardsFromArticleData()

            // Only update if different from current state
            if (!isEqual(articleCardBlocks, articleCards)) {
                setArticleCards(articleCardBlocks)
            }
        }
    }, [displayedArticles, generateCardsFromArticleData, articleCards])

    const isLoading = (articleTopicDataLoading && !queryAllArticles) || articlesDataLoading

    if (isLoading) {
        return (
            <div className="w-full my-20 flex justify-center">
                <Loader className="w-10 h-10" data-testid="dynamicGrid-Loader" />
            </div>
        )
    }
    const error = articleTopicDataError || articlesDataError

    if (error) {
        console.error('Failed to fetch articles:', error)
        return <div>There was an error</div>
    }

    const displayedArticleBlocks = articleCards.map((article, index) => (
        <div key={index}>
            <Blocks
                path={`component.options.articleCards.${index}.content`}
                blocks={[article]}
                parent={builderBlock?.id}
                data-testid="dynamicGrid-Blocks"
            />
        </div>
    ))

    const displayedItemsText = `Showing ${displayedArticlesCount} of ${totalItems?.length} articles`

    return (
        <PaginatedGridDisplay
            attributes={attributes}
            displayedItems={displayedArticleBlocks}
            displayedItemsText={displayedItemsText}
            showNextEntries={showNextEntries}
            showAllEntries={getAllEntries}
            showAll={showAll}
            allEntriesDisplayed={allEntriesDisplayed}
            data-testid="dynamicGrid-PaginatedGridDisplay"
        />
    )
}

DynamicGrid.propTypes = {
    attributes: PropTypes.object,
    builderBlock: PropTypes.object,
    articleCards: PropTypes.arrayOf(PropTypes.object),
    customGrid: PropTypes.bool,
    itemsToShowPerLoad: PropTypes.number
}
